import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { GridApi, GridOptions, RowNode, GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import { IRegionGroupListRecord, IUserProfile, allowedCommonRegionStatuses } from "../../npr-request.model";
import { ApiService } from "../../services";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { ModalService } from "../../shared/modal.service";
import { ToastrService } from "ngx-toastr";
import { ThirdPartyAllowlistColumnDefinition } from "./thirdparty-allowlist-column-definition";
import { SubscriptionRegionColumnDefinition } from "./subscription-region-column-definition";
import { CustomerRegion, Region } from "../../generated-models";
import { SubscriptionRegion } from "../../generated-models/SubscriptionRegion";
import { EnableRegionAccess } from "../../generated-models/EnableRegionAccess";
import { GridViewBaseComponent } from "../../utility/grid-view-base.component";
import { SharedDataService } from "../../services/sharedDataService";
import { map } from "rxjs/operators";

interface BackfillByConfig {
  name: string;
  identifier: BackfillIdentifier;
  regexPattern: string;
  regexErrorMessage: string;
}

enum BackfillIdentifier {
  BillingAccountId = "billingAccountId",
  TenantId = "tenantId"
}

@Component({
  standalone: false,
  templateUrl: "./admin-view-thirdparty-subscription-access.component.html",
  styleUrls: ["../../styles.scss"],
})
export class AdminViewThirdPartySubscriptionAccessComponent extends GridViewBaseComponent implements OnInit
{
  backfillByConfigs: BackfillByConfig[] = [
    {
      name: "Billing Account ID",
      identifier: BackfillIdentifier.BillingAccountId,
      regexPattern: "^[a-zA-Z0-9_:\\-]+$",
      regexErrorMessage: "Billing Account ID must be alphanumeric and can include '-', '_', and ':'"
    },
    {
      name: "Tenant ID",
      identifier: BackfillIdentifier.TenantId,
      regexPattern: "^[a-zA-Z0-9\\-]+$",
      regexErrorMessage: "Tenant ID must be alphanumeric and can include '-'"
    }
  ];

  gridOptions: GridOptions;
  initPageSize: number = 20;
  selectedRowCount = 0;
  selectedRows: CustomerRegion[] | SubscriptionRegion[] = [];
  rowData: CustomerRegion[] | SubscriptionRegion[] = [];
  userProfile: IUserProfile = null;
  keyFilterStats = "filter:thirdparty-subscription-access";

  backfillByNames = this.backfillByConfigs.map((config) => config.name);
  backfillByConfig = this.backfillByConfigs[0]
  backfillBy = this.backfillByConfig.name;

  backfillInput = "";
  backfillInputRegexPattern = this.backfillByConfig.regexPattern;
  backfillInputRegexErrorMessage = this.backfillByConfig.regexErrorMessage;

  regionsList: IRegionGroupListRecord[] = [];
  selectedRegions: string[] = [];

  subscriptionCountMessage = "";
  subscriptionCount = 0;
  showProceedButton = false;

  selectedTable = "CustomerRegion";
  tableOptions: string[] = ["CustomerRegion", "SubscriptionRegion"];

  gridApi: GridApi;

  constructor(
    protected modalService: ModalService,
    protected apiService: ApiService,
    protected loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    protected notificationService: ToastrService,
    private cdr: ChangeDetectorRef
  )
  {
    super(apiService, sharedDataService);
  }

  ngOnInit()
  {
    super.InitAgGrid(ThirdPartyAllowlistColumnDefinition);

    this.loadingService.setLoading(true);
    this.apiService
      .getUserProfile()
      .then((response) =>
      {
        this.userProfile = response;
        this.getRegionsList();
      })
      .catch((error) =>
      {
        this.notificationService.error(error);
      });
    this.loadingService.setLoading(false);
  }

  refreshData()
  {
    if (this.selectedTable === "CustomerRegion")
    {
      this.loadAllowlistData();
    }
    else
    {
      this.loadBackfilledSubscriptions();
    }
    this.setupFilterByLocalData();
    this.loadingService.setLoading(false);
  }

  loadAllowlistData()
  {
    this.apiService.getThirdPartyAllowlists().subscribe(
      (response: CustomerRegion[]) =>
      {
        if (response)
        {
          this.rowData = response;
          if (this.gridApi)
          {
            this.gridApi.setGridOption("rowData", this.rowData);
            this.gridApi.hideOverlay();
          }
        }
      },
      (error) =>
      {
        this.notificationService.error("Failed to load allowlist data");
        console.error(error);
        if (this.gridApi)
        {
          this.gridApi.hideOverlay();
        }
      }
    );
  }

  loadBackfilledSubscriptions()
  {
    this.apiService.getBackfilledSubscriptions().subscribe(
      (response: SubscriptionRegion[]) =>
      {
        if (response)
        {
          this.rowData = response;
          if (this.gridApi)
          {
            this.gridApi.setGridOption("rowData", this.rowData);
            this.gridApi.hideOverlay();
          }
        }
      },
      (error) =>
      {
        this.notificationService.error("Failed to load backfilled subscriptions");
        console.error(error);
        if (this.gridApi)
        {
          this.gridApi.hideOverlay();
        }
      }
    );
  }

  getRegionsList()
  {
    this.apiService
      .getRegionList()
      .pipe(map((regions) => regions.filter((region) => allowedCommonRegionStatuses.includes(region.Status))))
      .subscribe((response: Region[]) =>
      {
        if (response == null)
        {
          console.warn("No region is obtained from api server.");
        }
        else
        {
          this.regionsList = [];
          for (let i = 0; i < response.length; i++)
          {
            const rec: IRegionGroupListRecord = {} as IRegionGroupListRecord;
            rec.item_id = i + 1;
            rec.region = response[i].RegionName;
            rec.cloud = response[i].CloudName;
            this.regionsList.push(rec);
          }
        }
      });
  }

  onGridReady(params: GridReadyEvent)
  {
    super.onGridReady(params);
    this.refreshData();
  }

  onBackfillByChanged()
  {
    this.backfillByConfig = this.backfillByConfigs.find((config) => config.name === this.backfillBy);
    this.resetSubscriptionCount();
  }

  onBackfillInputChanged()
  {
    this.resetSubscriptionCount();
  }

  calculateSubscriptionCount()
  {
    // Validate not blank
    if (this.backfillInput.trim() === "")
    {
      this.notificationService.warning(`Please enter a ${this.backfillBy}.`);
      return;
    }

    // Validate using regex pattern
    const regex = new RegExp(this.backfillInputRegexPattern);
    if (!regex.test(this.backfillInput))
    {
      this.notificationService.error(this.backfillInputRegexErrorMessage);
      return;
    }

    this.loadingService.setLoading(true);

    this.resetSubscriptionCount();

    this.apiService
      .getThirdPartySubscriptionCount(this.backfillByConfig.identifier, this.backfillInput)
      .subscribe(
        (response) =>
        {
          this.subscriptionCount = response;
          this.subscriptionCountMessage = `${response} subscription${response !== 1 ? "s" : ""} found for ${this.backfillBy}: ${this.backfillInput}.`;

          if (response > 0)
          {
            this.showProceedButton = true;
          }

          this.loadingService.setLoading(false);
        },
        (error) =>
        {
          this.notificationService.error("Failed to calculate subscription count");
          console.error(error);
          this.subscriptionCountMessage = "";
          this.showProceedButton = false;
          this.loadingService.setLoading(false);
        }
      );
  }

  resetSubscriptionCount()
  {
    this.subscriptionCountMessage = "";
    this.subscriptionCount = 0;
    this.showProceedButton = false;
  }

  proceedToBackfill()
  {
    if (!this.backfillInput)
    {
      this.notificationService.warning(`Please provide ${this.backfillBy}`);
      return;
    }

    this.loadingService.setLoading(true);

    const payload: EnableRegionAccess = {
      BillingAccountId: this.backfillByConfig.identifier === BackfillIdentifier.BillingAccountId ? this.backfillInput : "",
      TenantId: this.backfillByConfig.identifier === BackfillIdentifier.TenantId ? this.backfillInput : "",
      Regions: this.selectedRegions,
    };

    this.apiService.enableThirdPartyRegionAccess(payload).subscribe(
      () =>
      {
        this.notificationService.success("Region access enabled successfully");
        this.loadingService.setLoading(false);
        this.refreshData();
      },
      (error) =>
      {
        this.notificationService.error("Failed to enable region access");
        console.error(error);
        this.loadingService.setLoading(false);
      }
    );
  }

  refreshGrid()
  {
    this.loadingService.setLoading(true);
    this.refreshData();
  }

  async deleteAllowlist()
  {
    const selectedRows: CustomerRegion[] = this.gridApi.getSelectedRows();
    if (selectedRows.length === 0)
    {
      this.notificationService.warning("Please select at least one row before deleting");
      return;
    }

    const entryLabel = selectedRows.length === 1 ? "entry" : "entries";

    try
    {
      await this.modalService.confirmationModal(`Are you sure you want to delete the selected allowlist ${entryLabel}?`);
    }
    catch
    {
      // For the modal dialog dismiss
      return;
    }

    this.loadingService.setLoading(true);

    const deletePromises = selectedRows.map((row) =>
    {
      return this.apiService
        .deleteThirdPartyAllowlist(row.BillingAccountId, row.Region)
        .toPromise();
    });

    Promise.all(deletePromises)
      .then(() =>
      {
        this.notificationService.success(`Allowlist ${entryLabel} deleted successfully`);
        this.refreshData();
      })
      .catch((error) =>
      {
        this.notificationService.error(`Failed to delete allowlist ${entryLabel}`);
        console.error(error);
        this.loadingService.setLoading(false);
      });
  }

  onSelectionChanged(event: SelectionChangedEvent)
  {
    this.selectedRows = [];
    this.selectedRowCount = event.api.getSelectedNodes().length;
    event.api.getSelectedNodes().forEach((row: RowNode) =>
    {
      this.selectedRows.push(row.data);
    });
    this.cdr.detectChanges();
  }

  onTableChanged()
  {
    this.selectedRows = [];

    if (this.gridApi)
    {
      const newColumnDefs = this.selectedTable === "CustomerRegion" ? ThirdPartyAllowlistColumnDefinition : SubscriptionRegionColumnDefinition;
      this.gridOptions.columnDefs = newColumnDefs;
      this.gridApi.setGridOption('columnDefs', newColumnDefs);
      this.gridApi.setGridOption("rowData", []);
      this.gridApi.showLoadingOverlay();
    }

    if (this.selectedTable === "CustomerRegion")
    {
      this.loadAllowlistData();
    }
    else
    {
      this.loadBackfilledSubscriptions();
    }
  }

  async insertRow()
  {
    try
    {
      const result = await this.modalService.insertCustomerRegionModal(this.regionsList);

      if (result)
      {
        this.loadingService.setLoading(true);

        this.apiService.addThirdPartyAllowlist(result.BillingAccountId, result.Region).subscribe(
          () =>
          {
            this.notificationService.success("Customer region entry added successfully");
            this.loadingService.setLoading(false);
            this.refreshData();
          },
          (error) =>
          {
            this.notificationService.error("Failed to add customer region entry");
            console.error(error);
            this.loadingService.setLoading(false);
          }
        );
      }
    }
    catch (error)
    {
      // Modal was dismissed/cancelled
      console.log("Insert cancelled");
    }
  }
}
